import React, { useEffect, useState } from 'react';
import { analysesAPI, SavedAnalysisSummary } from '../services/analysesAPI';

interface SaveMeta {
  granularity?: string;
  start_date?: string;
  end_date?: string;
  request?: any;
}

interface Props {
  currentResult: any | null;          // the analysisResults to save (null if none yet)
  saveMeta: SaveMeta | null;          // inputs/metadata for the current result
  onLoad: (result: any) => void;      // recall a stored result into the view
}

const fmtEur = (v: number) => '€' + Math.round(v || 0).toLocaleString();
const fmtSignedEur = (v: number) => (v >= 0 ? '+' : '−') + '€' + Math.abs(Math.round(v || 0)).toLocaleString();
const fmtDate = (s: string) => { try { return new Date(s).toLocaleString(); } catch { return s; } };

// Headline figures derived from a saved analysis result, for the compare view.
const analysisMetrics = (result: any) => {
  const rows: any[] = result?.cashflow_breakdown || [];
  const sum = (f: string) => rows.reduce((a, p) => a + (p[f] || 0), 0);
  const last = rows[rows.length - 1] || {};
  return {
    revenue: sum('revenue'),
    earningsBeforeTax: sum('earnings_before_tax'),
    netCashflow: typeof result?.total_net_cashflow === 'number' ? result.total_net_cashflow : sum('net_cashflow'),
    finalCumulative: last.cumulative_cashflow || 0,
  };
};
type Metrics = ReturnType<typeof analysisMetrics>;
const METRIC_ROWS: { key: keyof Metrics; label: string }[] = [
  { key: 'revenue', label: 'Total revenue' },
  { key: 'earningsBeforeTax', label: 'Earnings before tax' },
  { key: 'netCashflow', label: 'Net cashflow (operating)' },
  { key: 'finalCumulative', label: 'Final cumulative (after tax)' },
];

const SavedAnalysesPanel: React.FC<Props> = ({ currentResult, saveMeta, onLoad }) => {
  const [items, setItems] = useState<SavedAnalysisSummary[]>([]);
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [cmpA, setCmpA] = useState('');
  const [cmpB, setCmpB] = useState('');
  const [diff, setDiff] = useState<{ a: Metrics; b: Metrics; nameA: string; nameB: string } | null>(null);

  const refresh = async () => {
    try {
      setItems(await analysesAPI.list());
      setError(null);
    } catch (e: any) {
      setError('Could not load saved analyses (is the app running in full mode?)');
    }
  };

  useEffect(() => { refresh(); }, []);

  const save = async () => {
    if (!currentResult) return;
    const finalName = name.trim() || `Analysis ${new Date().toLocaleDateString()}`;
    setBusy(true);
    try {
      await analysesAPI.save({
        name: finalName,
        granularity: saveMeta?.granularity,
        start_date: saveMeta?.start_date,
        end_date: saveMeta?.end_date,
        property_count: saveMeta?.request?.properties?.length || 0,
        total_net_cashflow: currentResult?.total_net_cashflow || 0,
        request: saveMeta?.request,
        result: currentResult,
      });
      setName('');
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Failed to save analysis');
    } finally {
      setBusy(false);
    }
  };

  const load = async (id: string) => {
    setBusy(true);
    try {
      const rec = await analysesAPI.get(id);
      onLoad(rec.result);
      setError(null);
    } catch (e: any) {
      setError('Failed to load analysis');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this saved analysis?')) return;
    setBusy(true);
    try {
      await analysesAPI.remove(id);
      await refresh();
    } catch (e: any) {
      setError('Failed to delete analysis');
    } finally {
      setBusy(false);
    }
  };

  const compare = async () => {
    if (!cmpA || !cmpB || cmpA === cmpB) { setError('Pick two different saved analyses to compare.'); return; }
    setBusy(true);
    try {
      const [ra, rb] = await Promise.all([analysesAPI.get(cmpA), analysesAPI.get(cmpB)]);
      setDiff({
        a: analysisMetrics(ra.result), b: analysisMetrics(rb.result),
        nameA: items.find((i) => i.id === cmpA)?.name || 'A',
        nameB: items.find((i) => i.id === cmpB)?.name || 'B',
      });
      setError(null);
    } catch (e: any) {
      setError('Failed to load analyses for comparison');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-base font-semibold text-gray-800">Saved Analyses</h4>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={name}
            placeholder="Name this analysis"
            onChange={(e) => setName(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vista-teal-500"
          />
          <button
            onClick={save}
            disabled={!currentResult || busy}
            className="bg-vista-teal-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-vista-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title={currentResult ? 'Save the current analysis' : 'Run an analysis first'}
          >
            Save current
          </button>
        </div>
      </div>

      {error && <div className="text-xs text-red-600 mb-2">{error}</div>}

      {items.length === 0 ? (
        <div className="text-sm text-gray-500">No saved analyses yet. Run an analysis and click “Save current”.</div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {items.map((a) => (
            <li key={a.id} className="py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-medium text-gray-800 truncate">{a.name}</div>
                <div className="text-xs text-gray-500">
                  {fmtDate(a.created_at)} · {a.granularity || '—'} · {a.property_count} propert{a.property_count === 1 ? 'y' : 'ies'} · net {fmtEur(a.total_net_cashflow)}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => load(a.id)} disabled={busy}
                  className="text-vista-teal-600 hover:text-vista-teal-800 text-sm font-medium disabled:opacity-50">Load</button>
                <button onClick={() => remove(a.id)} disabled={busy}
                  className="text-red-500 hover:text-red-700 text-sm disabled:opacity-50">Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {items.length >= 2 && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <h5 className="text-sm font-semibold text-gray-700 mb-2">Compare two analyses</h5>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <select value={cmpA} onChange={(e) => setCmpA(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded-md text-sm">
              <option value="">Select A…</option>
              {items.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <span className="text-gray-400 text-sm">vs</span>
            <select value={cmpB} onChange={(e) => setCmpB(e.target.value)}
              className="px-2 py-1.5 border border-gray-300 rounded-md text-sm">
              <option value="">Select B…</option>
              {items.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <button type="button" onClick={compare} disabled={busy}
              className="bg-gray-700 text-white text-sm px-3 py-1.5 rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50">
              Compare
            </button>
          </div>
          {diff && (
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full text-sm divide-y divide-gray-200">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Metric</th>
                    <th className="px-3 py-2 text-right font-medium">{diff.nameA}</th>
                    <th className="px-3 py-2 text-right font-medium">{diff.nameB}</th>
                    <th className="px-3 py-2 text-right font-medium">Δ (B − A)</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {METRIC_ROWS.map((m) => {
                    const av = diff.a[m.key], bv = diff.b[m.key], d = bv - av;
                    return (
                      <tr key={m.key}>
                        <td className="px-3 py-2 text-gray-700">{m.label}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{fmtEur(av)}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{fmtEur(bv)}</td>
                        <td className={`px-3 py-2 text-right font-medium ${d >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {fmtSignedEur(d)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SavedAnalysesPanel;
